import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import DDRDashboard, { parseDDRWorkbook } from "./DDRDashboard";

/* ══════════════════════════════════════════════════════════════════════
   /affordability — signed-in users only.

   Visiting the URL checks for a valid session (konet_token, not expired);
   without one it redirects to the login page and comes back afterwards.
   Signed in, it loads the workbook shipped in /public and renders the
   dashboard. No upload, no publish step, and nothing cached:
   every visit re-reads the file, so replacing it in /public is all that is
   needed to update what everyone sees.

   To swap the workbook: drop a new file into /public and, if the name
   differs, change WORKBOOK below.
   ══════════════════════════════════════════════════════════════════════ */
const WORKBOOK = "ddr_outlets.xlsx";

/* ── Auth ── same localStorage keys the rest of the app writes at login.
   LOGIN_PATH is where signed-out visitors are sent; after login, send the
   user to localStorage[REDIRECT_KEY] (see consumePostLoginRedirect). */
const LOGIN_PATH   = "/";
/* Set to false once /api/affordability/workbook is live and the file has
   been removed from /public. */
const ALLOW_PUBLIC_FALLBACK = true;
const TOKEN_KEY    = "konet_token";
const EXPIRY_KEY   = "konet_token_expiry";
export const REDIRECT_KEY = "login_redirect_to";   // same key CallbackPage and DataRAGChat use

/* Expiry may be stored as epoch seconds, epoch ms, or an ISO date. */
function isExpired(raw) {
  if (!raw) return false;
  const n = Number(raw);
  const ms = Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : Date.parse(raw);
  return Number.isFinite(ms) && ms <= Date.now();
}

export function hasValidSession() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    if (isExpired(localStorage.getItem(EXPIRY_KEY))) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      return false;
    }
    return true;
  } catch { return false; }
}

/** Call right after a successful login: returns the page to go back to. */
export function consumePostLoginRedirect(fallback = "/") {
  try {
    const to = localStorage.getItem(REDIRECT_KEY);
    localStorage.removeItem(REDIRECT_KEY);
    return to || fallback;
  } catch { return fallback; }
}

const XLSX_CDN = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";

/* XLSX is a CDN global here, matching the rest of the app — it is not an
   npm dependency, so a bundler import would fail to resolve. */
let xlsxPromise = null;
function loadXLSX() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = XLSX_CDN;
    el.async = true;
    el.onload = () => (window.XLSX
      ? resolve(window.XLSX)
      : reject(new Error("XLSX loaded but window.XLSX is undefined")));
    el.onerror = () => { xlsxPromise = null; reject(new Error("Could not load the XLSX library")); };
    document.head.appendChild(el);
  });
  return xlsxPromise;
}

const BACKEND = process.env.REACT_APP_BACKEND || "http://localhost:8000";
const authHeader = () => {
  try { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; }
  catch { return {}; }
};

function clearSession() {
  try { [TOKEN_KEY, EXPIRY_KEY, "konet_user"].forEach(k => localStorage.removeItem(k)); } catch {}
}

/* Gate: a token must exist, be unexpired locally, AND be accepted by
   GET /api/auth/me. 401/403 → session cleared and sent to login. A network
   failure shows a retry instead of signing the user out. Re-checks every
   5 minutes and when the token changes in another tab. */
export function RequireAuth({ children }) {
  const location = useLocation();
  const [st, setSt] = useState(() => (hasValidSession() ? "checking" : "out"));

  const verify = useCallback(async () => {
    if (!hasValidSession()) { setSt("out"); return; }
    try {
      const r = await fetch(`${BACKEND}/api/auth/me`, { headers: authHeader() });
      if (r.status === 401 || r.status === 403) { clearSession(); setSt("out"); return; }
      if (!r.ok) throw new Error(`auth check failed (${r.status})`);
      const body = await r.json().catch(() => ({}));
      if (body?.user) { try { localStorage.setItem("konet_user", JSON.stringify(body.user)); } catch {} }
      setSt("in");
    } catch {
      setSt(prev => (prev === "in" ? "in" : "offline"));   // keep a verified session through blips
    }
  }, []);

  useEffect(() => {
    verify();
    const t = setInterval(verify, 5 * 60 * 1000);
    const onStorage = (e) => { if (e.key === TOKEN_KEY || e.key === EXPIRY_KEY) verify(); };
    window.addEventListener("storage", onStorage);
    return () => { clearInterval(t); window.removeEventListener("storage", onStorage); };
  }, [verify]);

  if (st === "out") {
    try { localStorage.setItem(REDIRECT_KEY, location.pathname + location.search); } catch {}
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }
  if (st === "checking") {
    return (
      <div style={wrap}><div style={{ textAlign: "center" }}>
        <div style={spinner} />
        <div style={{ color: "#5C1020", fontSize: 13, fontWeight: 600, marginTop: 16 }}>Checking your sign-in…</div>
        <style>{"@keyframes aff-spin{to{transform:rotate(360deg)}}"}</style>
      </div></div>
    );
  }
  if (st === "offline") {
    return (
      <div style={wrap}><div style={{ textAlign: "center" }}>
        <div style={{ color: "#1A0005", fontSize: 15, fontWeight: 700 }}>Can't reach the server to verify your sign-in</div>
        <button onClick={() => { setSt("checking"); verify(); }} style={{ marginTop: 14, background: "#E8001A",
          border: "none", color: "#fff", padding: "8px 18px", borderRadius: 8, fontSize: 12,
          fontWeight: 700, cursor: "pointer" }}>Try again</button>
      </div></div>
    );
  }
  return children;
}

export default function AffordabilityPage(props) {
  return <RequireAuth><AffordabilityDashboard {...props} /></RequireAuth>;
}

function AffordabilityDashboard({ session = null, onAskAI }) {
  const [state, setState] = useState({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      // Authenticated endpoint first; /public is only a fallback while the
      // backend route isn't deployed (delete the public copy once it is).
      let resp = await fetch(`${BACKEND}/api/affordability/workbook?t=${Date.now()}`,
        { headers: authHeader(), cache: "no-store" });
      if (resp.status === 401 || resp.status === 403) {
        clearSession();
        window.location.replace(LOGIN_PATH);
        return;
      }
      if (resp.status === 404 && ALLOW_PUBLIC_FALLBACK) {
        resp = await fetch(`${process.env.PUBLIC_URL || ""}/${WORKBOOK}?t=${Date.now()}`);
      }
      if (!resp.ok) {
        throw new Error(`Could not find /${WORKBOOK} (${resp.status}). `
          + `Place the workbook in the app's public folder.`);
      }

      const buf = await resp.arrayBuffer();
      // A dev server answers unknown paths with index.html, which would
      // otherwise fail deep inside XLSX.read with something opaque.
      const head = new TextDecoder().decode(new Uint8Array(buf.slice(0, 64)));
      if (/^\s*<!doctype|^\s*<html/i.test(head)) {
        throw new Error(`/${WORKBOOK} was not found — the server returned an HTML page. `
          + `Confirm the file sits in /public.`);
      }

      const XLSX = await loadXLSX();
      const wb = XLSX.read(buf, { type: "array" });
      const data = parseDDRWorkbook(wb, XLSX);
      if (!data) throw new Error("The workbook could not be parsed.");

      setState({ status: "ready", data });
    } catch (e) {
      setState({ status: "error", error: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.status === "loading") {
    return (
      <div style={wrap}>
        <div style={{ textAlign: "center" }}>
          <div style={spinner} />
          <div style={{ color: "#5C1020", fontSize: 13, fontWeight: 600, marginTop: 16 }}>
            Loading the affordability dashboard…
          </div>
          <div style={{ color: "#9A5060", fontSize: 11, marginTop: 4 }}>
            Reading {WORKBOOK}
          </div>
        </div>
        <style>{"@keyframes aff-spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div style={wrap}>
        <div style={{ background: "#fff", border: "1px solid #FADDDD", borderRadius: 12,
          padding: 32, maxWidth: 520, textAlign: "center",
          boxShadow: "0 2px 8px rgba(232,0,26,0.08)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1A0005" }}>
            Could not load the dashboard
          </div>
          <div style={{ fontSize: 12, color: "#9A5060", marginTop: 8, lineHeight: 1.6 }}>
            {state.error}
          </div>
          <button onClick={load} style={{ marginTop: 18, background: "#E8001A",
            border: "none", color: "#fff", padding: "8px 18px", borderRadius: 8,
            fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Try again</button>
        </div>
      </div>
    );
  }

  return <DDRDashboard data={state.data} session={session} onAskAI={onAskAI} />;
}

const wrap = {
  minHeight: "100vh", background: "#FFF8F8", display: "flex",
  alignItems: "center", justifyContent: "center", padding: 40,
};
const spinner = {
  width: 34, height: 34, margin: "0 auto",
  border: "3px solid #FADDDD", borderTopColor: "#E8001A",
  borderRadius: "50%", animation: "aff-spin 0.8s linear infinite",
};